import useAsyncAction from 'hooks/useAsyncAction'
import { Feed, FileFolder, InteractionTrackingSource, InteractionTrackingSourceType, Stream } from 'interfaces'
import { QuoteFeed } from 'interfaces/Content/QuoteFeed'
import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router'
import { Observable, Subscription, from } from 'rxjs'
import { addRecentSource, getRecentSources, removeRecentlyViewed } from 'services/content/recommended/actions'
import { recentSourcesSelector } from 'services/content/selectors'
import StoreState, { StoreThunkDispatch } from 'store/state'
import RecentlyViewedCarousel from './RecentlyViewedCarousel'

interface RecentlyViewedCarouselContainerOwnProps {
  navPrefix?: string
  title?: string
  onRecentSourcesFetched?: () => void
}

interface RecentlyViewedCarouselContainerConnectedProps {
  sources: Array<Feed | Stream | FileFolder>
  fetchRecentSources: (page: number) => Observable<any[]>
  recordRecentSource: (source: Feed | Stream | FileFolder) => void
  removeSource: (type: InteractionTrackingSourceType, source: InteractionTrackingSource) => Observable<any>
}

export type RecentlyViewedCarouselContainerProps = RecentlyViewedCarouselContainerOwnProps
  & RecentlyViewedCarouselContainerConnectedProps

function RecentlyViewedCarouselContainer(props: RecentlyViewedCarouselContainerProps) {
  const location = useLocation()
  const [triggerSourceRemove] = useAsyncAction(props.removeSource)
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let subscription: Subscription
    if (!loading && hasNextPage) {
      setLoading(true)
      subscription = props.fetchRecentSources(page).subscribe((sources: any[]) => {
        setLoading(false)
        if (page === 1 && props.onRecentSourcesFetched) {
          props.onRecentSourcesFetched()
        }

        if (sources.length === 0) {
          setHasNextPage(false)
        }
      }, () => {
        if (page === 1 && props.onRecentSourcesFetched) {
          props.onRecentSourcesFetched()
        }
      })
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
    }
    // EXPL: pagination effect keyed on `page` only. `loading`/`hasNextPage` are guards read at
    // trigger time — listing `loading` would re-run on setLoading(true) and unsubscribe the
    // in-flight fetch. fetchRecentSources is connect-stable; onRecentSourcesFetched fires post-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const fetchNextPage = () => {
    if (!loading) {
      setPage(page => page + 1)
    }
  }

  const onSourceClick = (_type: 'stream' | 'feed' | 'file-folder' | 'quote_feed', source: Feed | Stream | FileFolder) => {
    props.recordRecentSource(source)
  }

  const removeSource = (type: InteractionTrackingSourceType, source: InteractionTrackingSource) => {

    triggerSourceRemove(type, source)
  }

  if (!loading && props.sources.length === 0) {
    return null
  }

  return (
    <RecentlyViewedCarousel
      title={props.title}
      sources={props.sources}
      loading={loading}
      location={location.pathname}
      navPrefix={props.navPrefix}
      onSourceClick={onSourceClick}
      onRemoveSource={removeSource}
      onScrollLimit={fetchNextPage}
    />
  )
}

function mapStateToProps(state: StoreState) {
  return {
    sources: recentSourcesSelector(state) as any[]
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    fetchRecentSources: (page: number) => from(dispatch(getRecentSources(page)).unwrap().then(res => res.items)),
    recordRecentSource: (source: Feed | Stream | FileFolder | QuoteFeed) => dispatch(addRecentSource(source)),
    removeSource: (type: InteractionTrackingSourceType, source: InteractionTrackingSource) => {
      return from(dispatch(removeRecentlyViewed({ type, source })).unwrap())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RecentlyViewedCarouselContainer)
